import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation & Common
      'nav.dashboard': 'Dashboard',
      'nav.services': 'Services',
      'nav.products': 'Products',
      'nav.bids': 'Live Bids',
      'nav.communications': 'Communications',
      'nav.analytics': 'Analytics',
      'nav.profile': 'Profile',
      'nav.inbox': 'Inbox',
      'nav.logout': 'Logout',
      'nav.admin': 'Admin Dashboard',
      'nav.supervisor': 'Supervisor Dashboard',
      
      // Authentication
      'auth.signIn': 'Sign In',
      'auth.signUp': 'Sign Up',
      'auth.email': 'Email',
      'auth.password': 'Password',
      'auth.confirmPassword': 'Confirm Password',
      'auth.name': 'Full Name',
      'auth.company': 'Company',
      'auth.phone': 'Phone Number',
      'auth.location': 'Location',
      'auth.userType': 'User Type',
      'auth.customer': 'Customer',
      'auth.provider': 'Service Provider',
      'auth.trade': 'Select Trade',
      'auth.termsAndConditions': 'Terms and Conditions',
      'auth.privacyPolicy': 'Privacy Policy',
      'auth.contactSupport': 'Contact Support',
      'auth.forgotPassword': 'Forgot Password?',
      'auth.alreadyHaveAccount': 'Already have an account?',
      'auth.dontHaveAccount': "Don't have an account?",
      
      // Dashboard
      'dashboard.title': 'D.C. Technology Corp.',
      'dashboard.subtitle': 'Your electrical marketplace',
      'dashboard.welcome': 'Welcome back',
      'dashboard.searchPlaceholder': 'Search services, products, providers...',
      'dashboard.advancedFilters': 'Advanced Filters',
      'dashboard.hideFilters': 'Hide Filters',
      'dashboard.availableServices': 'Available Services',
      'dashboard.equipmentProducts': 'Equipment & Products',
      'dashboard.providerInfo': 'Provider Info',
      'dashboard.suppliesEquipment': 'Supplies & Equipment',
      'dashboard.liveBids': 'Live Bids',
      'dashboard.noResults': 'No results found',
      'dashboard.loading': 'Loading...',
      
      // Communications
      'comm.messages': 'Messages',
      'comm.videoCalls': 'Video Calls',
      'comm.reviews': 'Reviews & Ratings',
      'comm.chatPlaceholder': 'Type a message...',
      'comm.startVideoCall': 'Start Video Call',
      'comm.endCall': 'End Call',
      'comm.videoCallActive': 'Video call active',
      'comm.connectWithProviders': 'Connect with customers and providers through real-time messaging',
      'comm.startVideoCalls': 'Start video calls with providers after bid acceptance',
      'comm.shareExperience': 'Share your experience and view feedback from others',
      'comm.openMessages': 'Open Messages',
      'comm.viewReviews': 'View Reviews & Write Review',
      
      // Booking System
      'booking.title': 'Advanced Booking System',
      'booking.selectDate': 'Select Date',
      'booking.availableSlots': 'Available Time Slots for {{date}}',
      'booking.estimatedDuration': 'Estimated Duration (hours)',
      'booking.recurringBooking': 'Make this a recurring booking',
      'booking.repeatPattern': 'Repeat Pattern',
      'booking.endDate': 'End Date',
      'booking.weekly': 'Weekly',
      'booking.biweekly': 'Bi-weekly',
      'booking.monthly': 'Monthly',
      'booking.specialInstructions': 'Special Instructions or Notes',
      'booking.instructionsPlaceholder': 'Any special requirements, access instructions, or notes for the provider...',
      'booking.providerInfo': 'Provider Information',
      'booking.typicalHours': 'Typical Hours',
      'booking.serviceArea': 'Service Area',
      'booking.responseTime': 'Response Time',
      'booking.bookingSummary': 'Booking Summary',
      'booking.date': 'Date',
      'booking.time': 'Time',
      'booking.duration': 'Duration',
      'booking.recurring': 'Recurring',
      'booking.submitRequest': 'Submit Booking Request',
      'booking.createRecurring': 'Create Recurring Booking',
      'booking.creating': 'Creating Booking...',
      'booking.noSlotsAvailable': 'No available slots for this date. Please select another date.',
      'booking.schedulingConflicts': 'Scheduling Conflicts:',
      'booking.incompleteSelection': 'Please select both date and time slot',
      'booking.submitted': 'Booking Submitted!',
      'booking.recurringCreated': 'Recurring booking created successfully',
      'booking.requestSent': 'Your booking request has been sent to the provider',
      'booking.failed': 'Booking Failed',
      
      // Reviews
      'review.overallRating': 'Overall Rating',
      'review.rateAspects': 'Rate specific aspects (optional)',
      'review.workQuality': 'Work Quality',
      'review.timeliness': 'Timeliness',
      'review.communication': 'Communication',
      'review.professionalism': 'Professionalism',
      'review.valueForMoney': 'Value for Money',
      'review.cleanliness': 'Cleanliness',
      'review.yourReview': 'Your Review',
      'review.shareExperience': 'Share your experience with this service...',
      'review.addPhotos': 'Add Photos (optional)',
      'review.addPhoto': 'Add Photo',
      'review.uploadPhotos': 'Upload up to 5 photos. JPG, PNG formats supported.',
      'review.cancel': 'Cancel',
      'review.submit': 'Submit Review',
      'review.submitting': 'Submitting...',
      'review.writeReview': 'Write a Review',
      'review.customerReviews': 'Customer Reviews',
      'review.helpful': 'Helpful',
      'review.notHelpful': 'Not Helpful',
      'review.anonymous': 'Anonymous',
      
      // Analytics
      'analytics.title': 'Analytics Dashboard',
      'analytics.totalEarnings': 'Total Earnings',
      'analytics.activeServices': 'Active Services',
      'analytics.newBookings': 'New Bookings',
      'analytics.averageRating': 'Average Rating',
      'analytics.totalUsers': 'Total Users',
      'analytics.totalRevenue': 'Total Revenue',
      'analytics.activeOrders': 'Active Orders',
      'analytics.platformActivity': 'Platform Activity',
      'analytics.totalSpent': 'Total Spent',
      'analytics.ordersPlaced': 'Orders Placed',
      'analytics.servicesUsed': 'Services Used',
      'analytics.favoriteProviders': 'Favorite Providers',
      'analytics.performanceTrends': 'Performance Trends',
      'analytics.recentActivity': 'Recent Activity',
      'analytics.aiInsights': 'AI Insights & Predictions',
      'analytics.vsLastPeriod': 'vs last period',
      'analytics.7days': '7 Days',
      'analytics.30days': '30 Days',
      'analytics.90days': '90 Days',
      'analytics.1year': '1 Year',
      
      // Invoicing
      'invoice.title': 'Automated Invoicing',
      'invoice.generateInvoice': 'Generate Invoice',
      'invoice.invoiceNumber': 'Invoice Number',
      'invoice.issueDate': 'Issue Date',
      'invoice.dueDate': 'Due Date',
      'invoice.billTo': 'Bill To',
      'invoice.description': 'Description',
      'invoice.quantity': 'Quantity',
      'invoice.rate': 'Rate',
      'invoice.amount': 'Amount',
      'invoice.subtotal': 'Subtotal',
      'invoice.tax': 'Tax',
      'invoice.total': 'Total',
      'invoice.paid': 'Paid',
      'invoice.pending': 'Pending',
      'invoice.overdue': 'Overdue',
      'invoice.downloadPdf': 'Download PDF',
      'invoice.sendEmail': 'Send via Email',
      'invoice.markPaid': 'Mark as Paid',
      'invoice.paymentTerms': 'Payment Terms',
      'invoice.notes': 'Notes',
      
      // Filters & Search
      'filter.category': 'Category',
      'filter.allCategories': 'All Categories',
      'filter.location': 'Location',
      'filter.allLocations': 'All Locations',
      'filter.nearby': 'Nearby',
      'filter.priceRange': 'Price Range',
      'filter.minPrice': 'Min $',
      'filter.maxPrice': 'Max $',
      'filter.rating': 'Rating & Sort',
      'filter.allRatings': 'All Ratings',
      'filter.4plusStars': '4+ Stars',
      'filter.3plusStars': '3+ Stars',
      'filter.sortBy': 'Sort By',
      'filter.newest': 'Newest First',
      'filter.oldest': 'Oldest First',
      'filter.priceLowHigh': 'Price: Low to High',
      'filter.priceHighLow': 'Price: High to Low',
      'filter.highestRated': 'Highest Rated',
      'filter.inStockOnly': 'In Stock Only',
      'filter.clearFilters': 'Clear Filters',
      
      // Common Actions
      'action.save': 'Save',
      'action.edit': 'Edit',
      'action.delete': 'Delete',
      'action.cancel': 'Cancel',
      'action.confirm': 'Confirm',
      'action.close': 'Close',
      'action.submit': 'Submit',
      'action.loading': 'Loading...',
      'action.retry': 'Retry',
      'action.back': 'Back',
      'action.next': 'Next',
      'action.previous': 'Previous',
      'action.search': 'Search',
      'action.filter': 'Filter',
      'action.clear': 'Clear',
      'action.select': 'Select',
      'action.upload': 'Upload',
      'action.download': 'Download',
      
      // Status Messages
      'status.success': 'Success',
      'status.error': 'Error',
      'status.warning': 'Warning',
      'status.info': 'Information',
      'status.pending': 'Pending',
      'status.completed': 'Completed',
      'status.inProgress': 'In Progress',
      'status.cancelled': 'Cancelled',
      'status.failed': 'Failed',
      
      // Time & Date
      'time.justNow': 'Just now',
      'time.minutesAgo': '{{count}} minutes ago',
      'time.hoursAgo': '{{count}} hours ago',
      'time.daysAgo': '{{count}} days ago',
      'time.today': 'Today',
      'time.yesterday': 'Yesterday',
      'time.tomorrow': 'Tomorrow',
      
      // Company Info
      'company.name': 'D.C. Technology Corp.',
      'company.tagline': 'Your electrical marketplace',
      'company.email': 'Corporate.dccorp@gmail.com'
    }
  },
  es: {
    translation: {
      // Navigation & Common
      'nav.dashboard': 'Panel de Control',
      'nav.services': 'Servicios',
      'nav.products': 'Productos',
      'nav.bids': 'Ofertas en Vivo',
      'nav.communications': 'Comunicaciones',
      'nav.analytics': 'Analíticas',
      'nav.profile': 'Perfil',
      'nav.inbox': 'Bandeja de Entrada',
      'nav.logout': 'Cerrar Sesión',
      'nav.admin': 'Panel de Administrador',
      'nav.supervisor': 'Panel Supervisor',
      
      // Authentication
      'auth.signIn': 'Iniciar Sesión',
      'auth.signUp': 'Registrarse',
      'auth.email': 'Correo Electrónico',
      'auth.password': 'Contraseña',
      'auth.confirmPassword': 'Confirmar Contraseña',
      'auth.name': 'Nombre Completo',
      'auth.company': 'Empresa',
      'auth.phone': 'Número de Teléfono',
      'auth.location': 'Ubicación',
      'auth.userType': 'Tipo de Usuario',
      'auth.customer': 'Cliente',
      'auth.provider': 'Proveedor de Servicios',
      'auth.trade': 'Seleccionar Oficio',
      'auth.termsAndConditions': 'Términos y Condiciones',
      'auth.privacyPolicy': 'Política de Privacidad',
      'auth.contactSupport': 'Contactar Soporte',
      'auth.forgotPassword': '¿Olvidaste tu contraseña?',
      'auth.alreadyHaveAccount': '¿Ya tienes una cuenta?',
      'auth.dontHaveAccount': '¿No tienes una cuenta?',
      
      // Dashboard
      'dashboard.title': 'D.C. Technology Corp.',
      'dashboard.subtitle': 'Tu mercado eléctrico',
      'dashboard.welcome': 'Bienvenido de nuevo',
      'dashboard.searchPlaceholder': 'Buscar servicios, productos, proveedores...',
      'dashboard.advancedFilters': 'Filtros Avanzados',
      'dashboard.hideFilters': 'Ocultar Filtros',
      'dashboard.availableServices': 'Servicios Disponibles',
      'dashboard.equipmentProducts': 'Equipos y Productos',
      'dashboard.providerInfo': 'Información del Proveedor',
      'dashboard.suppliesEquipment': 'Suministros y Equipos',
      'dashboard.liveBids': 'Ofertas en Vivo',
      'dashboard.noResults': 'No se encontraron resultados',
      'dashboard.loading': 'Cargando...',
      
      // Communications
      'comm.messages': 'Mensajes',
      'comm.videoCalls': 'Videollamadas',
      'comm.reviews': 'Reseñas y Calificaciones',
      'comm.chatPlaceholder': 'Escribe un mensaje...',
      'comm.startVideoCall': 'Iniciar Videollamada',
      'comm.endCall': 'Finalizar Llamada',
      'comm.videoCallActive': 'Videollamada activa',
      'comm.connectWithProviders': 'Conecta con clientes y proveedores mediante mensajería en tiempo real',
      'comm.startVideoCalls': 'Inicia videollamadas con proveedores después de la aceptación de ofertas',
      'comm.shareExperience': 'Comparte tu experiencia y ve comentarios de otros',
      'comm.openMessages': 'Abrir Mensajes',
      'comm.viewReviews': 'Ver Reseñas y Escribir Reseña',
      
      // Company Info
      'company.name': 'D.C. Technology Corp.',
      'company.tagline': 'Tu mercado eléctrico',
      'company.email': 'Corporate.dccorp@gmail.com'
    }
  },
  fr: {
    translation: {
      // Navigation & Common
      'nav.dashboard': 'Tableau de Bord',
      'nav.services': 'Services',
      'nav.products': 'Produits',
      'nav.bids': 'Offres en Direct',
      'nav.communications': 'Communications',
      'nav.analytics': 'Analyses',
      'nav.profile': 'Profil',
      'nav.inbox': 'Boîte de Réception',
      'nav.logout': 'Déconnexion',
      'nav.admin': 'Tableau Admin',
      'nav.supervisor': 'Tableau Superviseur',
      
      // Authentication
      'auth.signIn': 'Se Connecter',
      'auth.signUp': 'S\'inscrire',
      'auth.email': 'Email',
      'auth.password': 'Mot de Passe',
      'auth.confirmPassword': 'Confirmer le Mot de Passe',
      'auth.name': 'Nom Complet',
      'auth.company': 'Entreprise',
      'auth.phone': 'Numéro de Téléphone',
      'auth.location': 'Emplacement',
      'auth.userType': 'Type d\'Utilisateur',
      'auth.customer': 'Client',
      'auth.provider': 'Fournisseur de Services',
      'auth.trade': 'Sélectionner le Métier',
      'auth.termsAndConditions': 'Termes et Conditions',
      'auth.privacyPolicy': 'Politique de Confidentialité',
      'auth.contactSupport': 'Contacter le Support',
      'auth.forgotPassword': 'Mot de passe oublié?',
      'auth.alreadyHaveAccount': 'Vous avez déjà un compte?',
      'auth.dontHaveAccount': 'Vous n\'avez pas de compte?',
      
      // Dashboard
      'dashboard.title': 'D.C. Technology Corp.',
      'dashboard.subtitle': 'Votre marché électrique',
      'dashboard.welcome': 'Bon retour',
      'dashboard.searchPlaceholder': 'Rechercher services, produits, fournisseurs...',
      'dashboard.advancedFilters': 'Filtres Avancés',
      'dashboard.hideFilters': 'Masquer les Filtres',
      'dashboard.availableServices': 'Services Disponibles',
      'dashboard.equipmentProducts': 'Équipements et Produits',
      'dashboard.providerInfo': 'Informations du Fournisseur',
      'dashboard.suppliesEquipment': 'Fournitures et Équipements',
      'dashboard.liveBids': 'Offres en Direct',
      'dashboard.noResults': 'Aucun résultat trouvé',
      'dashboard.loading': 'Chargement...',
      
      // Communications
      'comm.messages': 'Messages',
      'comm.videoCalls': 'Appels Vidéo',
      'comm.reviews': 'Avis et Évaluations',
      'comm.chatPlaceholder': 'Tapez un message...',
      'comm.startVideoCall': 'Démarrer Appel Vidéo',
      'comm.endCall': 'Terminer l\'Appel',
      'comm.videoCallActive': 'Appel vidéo actif',
      'comm.connectWithProviders': 'Connectez-vous avec les clients et fournisseurs via la messagerie en temps réel',
      'comm.startVideoCalls': 'Démarrez des appels vidéo avec les fournisseurs après l\'acceptation des offres',
      'comm.shareExperience': 'Partagez votre expérience et consultez les commentaires des autres',
      'comm.openMessages': 'Ouvrir les Messages',
      'comm.viewReviews': 'Voir les Avis et Écrire un Avis',
      
      // Company Info
      'company.name': 'D.C. Technology Corp.',
      'company.tagline': 'Votre marché électrique',
      'company.email': 'Corporate.dccorp@gmail.com'
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    
    interpolation: {
      escapeValue: false,
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'dcTech_language',
      caches: ['localStorage'],
    },
  });

export default i18n;